import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { IconMap } from '../lib';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  subject: string;
  message: string;
}

interface ContactProps {}

const Contact: React.FC<ContactProps> = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ show: false, message: '', type: 'info' });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const serviceOptions = [
    { value: '', label: 'Select a service' },
    { value: 'Breakfast Club', label: 'Breakfast Club' },
    { value: 'Sessional Care', label: 'Sessional Care' },
    { value: '2PM - 3PM Club', label: '2PM - 3PM Club' },
    { value: 'After-School Programme', label: 'After-School Programme' },
    { value: 'General Enquiry', label: 'General Enquiry' }
  ];

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'info' });
    }, 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        service: formData.service,
        subject: formData.subject,
        message: formData.message.replace(/\n/g, '<br>')
      };

      await emailjs.send(
        'service_zdy0iec',
        'template_xejuppq',
        templateParams,
        'R76ZGiGBr-pStwIGH'
      );

      showNotification('Thank you for your message! We\'ll get back to you soon.', 'success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS error:', error);
      showNotification('Sorry, there was an error sending your message. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: IconMap.faMapMarkedAlt,
      title: "Our Location",
      content: "Brocca Road, Mucklagh, Tullamore, Co Offaly, R35 W568",
      href: "https://www.google.com/maps/search/?api=1&query=Childs+Play+Pre+School+Brocca+Rd+Mucklagh+Co+Offaly"
    },
    {
      icon: IconMap.faPhone,
      title: "Phone Number",
      content: "+353 86 878 4903",
      href: "tel:+353868784903"
    },
    {
      icon: IconMap.faEnvelope,
      title: "Email Address",
      content: "info@childsplaymucklagh.ie",
      href: "mailto:info@childsplaymucklagh.ie"
    },
    {
      icon: IconMap.faClock,
      title: "Opening Hours",
      content: "Mon - Thu: 8:00 AM - 6:00 PM | Fri: 8:00 AM - 5:00 PM"
    }
  ];

  return (
    <section className="contact bg-[#e0f2f1]" id="contact">
      <div className="body-container py-16 px-4 sm:px-6 lg:px-8">
        <div className="contact-content">
          <div className="contact-info" data-aos="fade-right">
            <div className="section-badge">Get in Touch</div>
            <h2>We'd Love to Hear From You</h2>
            <p>
              Ready to give your child the best start in life? Contact us today to schedule a visit, 
              ask questions, or learn more about our services. We're here to support your family.
            </p>
            
            <div className="contact-details">
              {contactInfo.map((item, index) =>
                item.href ? (
                  <a
                    key={index}
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="contact-item"
                  >
                    <div className="contact-icon">
                      <FontAwesomeIcon
                        className="text-2xl text-white"
                        icon={item.icon as IconDefinition}
                      />
                    </div>
                    <div className="contact-text">
                      <h4>{item.title}</h4>
                      <p>{item.content}</p>
                    </div>
                  </a>
                ) : (
                  <div key={index} className="contact-item">
                    <div className="contact-icon">
                      <FontAwesomeIcon
                        className="text-2xl text-white"
                        icon={item.icon as IconDefinition}
                      />
                    </div>
                    <div className="contact-text">
                      <h4>{item.title}</h4>
                      <p>{item.content}</p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          
          <div className="contact-form" data-aos="fade-left">
            <div className="form">
              <h3>Send us a Message</h3>
              <p className="form-subtitle">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder=" "
                      required
                    />
                    <label>Your Name</label>
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder=" "
                      required
                    />
                    <label>Email Address</label>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder=" "
                    />
                    <label>Phone Number</label>
                  </div>
                  <div className="form-group">
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className={formData.service === '' ? 'placeholder' : ''}
                    >
                      {serviceOptions.map(option => (
                        <option key={option.value} value={option.value} disabled={option.value === ''}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <label>Enquiry Type</label>
                  </div>
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                  />
                  <label>Subject</label>
                </div>
                
                <div className="form-group">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                    rows={5}
                  />
                  <label>Your Message</label>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <i className="fas fa-paper-plane"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      
      {notification.show && (
        <div className={`notification ${notification.type} show`}>
          {notification.message}
        </div>
      )}
    </section>
  );
};

export default Contact; 